/**
 * Roster — landing page. Lean student cards from GET /teacher/students.
 * Sortable; highlights students needing attention.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiAlertCircle, FiChevronRight } from 'react-icons/fi';
import teacherAPI from '../services/api';

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d)) return Infinity;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function needsAttention(s) {
  const inactive = daysSince(s.lastActive) > 7;
  const lowAccuracy = s.accuracy !== null && s.accuracy < 50;
  return inactive || lowAccuracy;
}

const SORTS = {
  name: (a, b) => (a.displayName || '').localeCompare(b.displayName || ''),
  level: (a, b) => (b.level || 0) - (a.level || 0),
  accuracy: (a, b) => (b.accuracy ?? -1) - (a.accuracy ?? -1),
  active: (a, b) => daysSince(a.lastActive) - daysSince(b.lastActive),
  messages: (a, b) => (b.messagesThisWeek || 0) - (a.messagesThisWeek || 0),
};

function Roster({ setPageTitle, refreshKey }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [schoolName, setSchoolName] = useState('');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('active');

  useEffect(() => { setPageTitle('Students'); }, [setPageTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await teacherAPI.getStudents();
      setStudents(data.students || []);
      setSchoolName(data.school?.name || '');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return <div className="loading" style={{ minHeight: '60vh' }}><div className="loading-spinner" /></div>;
  }

  if (error) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><FiAlertCircle /></div>
        <div className="empty-state-title">Could not load students</div>
        <div className="empty-state-text">{error}</div>
      </div>
    );
  }

  const filtered = students
    .filter((s) =>
      !query ||
      (s.displayName || '').toLowerCase().includes(query.toLowerCase()) ||
      (s.email || '').toLowerCase().includes(query.toLowerCase())
    )
    .sort(SORTS[sortKey] || SORTS.name);

  const attentionCount = students.filter(needsAttention).length;
  const activeThisWeek = students.filter((s) => (s.messagesThisWeek || 0) > 0).length;
  const avgAccuracy = (() => {
    const vals = students.map((s) => s.accuracy).filter((v) => v !== null && v !== undefined);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  })();

  return (
    <div>
      {/* Summary stat cards */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card-label">Total Students</div>
          <div className="stat-card-value">{students.length}</div>
          <div className="stat-card-label">{schoolName}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Active This Week</div>
          <div className="stat-card-value">{activeThisWeek}</div>
          <div className="stat-card-label">practiced in last 7 days</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Need Attention</div>
          <div className="stat-card-value" style={{ color: attentionCount ? '#ff6b6b' : undefined }}>
            {attentionCount}
          </div>
          <div className="stat-card-label">inactive or low accuracy</div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: 12, opacity: 0.5 }} />
          <input
            className="form-input"
            style={{ paddingLeft: 36 }}
            placeholder="Search students by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          className="form-input"
          style={{ maxWidth: 220 }}
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
        >
          <option value="active">Sort: Recently Active</option>
          <option value="name">Sort: Name</option>
          <option value="level">Sort: Level (high→low)</option>
          <option value="accuracy">Sort: Accuracy (high→low)</option>
          <option value="messages">Sort: Messages this week</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FiSearch /></div>
          <div className="empty-state-title">No students found</div>
          <div className="empty-state-text">
            {students.length === 0
              ? 'No students are linked to this school yet.'
              : 'Try a different search term.'}
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: 'rgba(255,255,255,0.03)' }}>
                <th style={th}>Student</th>
                <th style={th}>Level</th>
                <th style={th}>Accuracy</th>
                <th style={th}>Streak</th>
                <th style={th}>Last Active</th>
                <th style={th}>Msgs / wk</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const attn = needsAttention(s);
                const since = daysSince(s.lastActive);
                return (
                  <tr
                    key={s.uid}
                    onClick={() => navigate(`/student/${s.uid}`)}
                    style={{
                      cursor: 'pointer',
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      background: attn ? 'rgba(255,107,107,0.05)' : undefined,
                    }}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{s.displayName}</div>
                      <div style={{ fontSize: 12, opacity: 0.5 }}>{s.email}</div>
                    </td>
                    <td style={td}>
                      <span className="badge badge-info">Lvl {s.level}</span>
                      <span style={{ fontSize: 12, opacity: 0.5, marginLeft: 6 }}>{s.xp} XP</span>
                    </td>
                    <td style={td}>
                      {s.accuracy === null ? (
                        <span style={{ opacity: 0.4 }}>—</span>
                      ) : (
                        <span className={`badge ${s.accuracy >= 70 ? 'badge-success' : s.accuracy >= 50 ? 'badge-warning' : 'badge-danger'}`}>
                          {s.accuracy}%
                        </span>
                      )}
                    </td>
                    <td style={td}>{s.streak > 0 ? `🔥 ${s.streak}` : '—'}</td>
                    <td style={td}>
                      {since === Infinity ? (
                        <span style={{ opacity: 0.4 }}>never</span>
                      ) : since === 0 ? (
                        <span style={{ color: '#30d158' }}>today</span>
                      ) : since > 7 ? (
                        <span style={{ color: '#ff6b6b' }}>{since}d ago</span>
                      ) : (
                        `${since}d ago`
                      )}
                    </td>
                    <td style={td}>{s.messagesThisWeek ?? '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {attn && <FiAlertCircle style={{ color: '#ff6b6b', marginRight: 8 }} title="Needs attention" />}
                      <FiChevronRight style={{ opacity: 0.4 }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {avgAccuracy !== null && (
        <p style={{ marginTop: 16, fontSize: 13, opacity: 0.5, textAlign: 'center' }}>
          Class average accuracy: {avgAccuracy}% · Click any student for detailed progress &amp; mistakes
        </p>
      )}
    </div>
  );
}

const th = { padding: '14px 16px', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.6 };
const td = { padding: '14px 16px', fontSize: 14 };

export default Roster;
